import styled, { css } from 'styled-components';

import { NewTableRibbonProps } from './NewTableRibbon';

import { elevationTokens } from '../../../../tokens/globals/tokens';
import { newTableTokens } from '../../../../tokens/components/NewTable/tokens';

import { StyledHeader } from '../NewTableHeadCell/NewTableHeadCell';

/*
  Accessibility concerns
  - Triangle border is a psuedo element with 0x0 dimensions which are meant
    to be decorative and not read aloud by screen readers. It isn't accessible by backstop.js or keyboard navigation for the same reason.
  - Pop out text cannot be highlighted

  Layering with pseudo elements clash with features like frozen columns
  - All Ribbon triggers should load BEHIND the header.
  - [KNOWN BUG] (scrollable tables without frozen columns)
    The first row's Ribbon pop out gets special treatment to load
    ABOVE the header. Previously, both triggers and pop outs were loading
    above the header. As of this note, the only funny looking instance is if
    a user continues to hover over the first row/cell/self when scrolling,
    the pop out can be seen over the header. (Refer to PR for visual)
  - [KNOWN BUG] (tables with frozen columns)
    The first row's Ribbon pop out is blocked by the header. The problem
    is that frozen columns are -also- sticky creating a zindex clash.

  How about using Popper? (like in Tooltips)
  - A time box attempt was made and it does solve the zindex clash in all tables
    BUT requires additional logic to NewTableCells and NewTableRows to retain the current hovering option.

  Token notes
  - The current top/bottom padding is 7px rather than a multiple of 4. This is
    so that the first row's Ribbon can fit over the table header (otherwise
    it's cut off)

  API notes
  - Use semantic variants instead of a color prop for consistent color usage
  - "Purple" and "Pink" do not exist in the new palette. The API will update
    when we come around to work on semantics for this component.

  See PR for more detail: https://github.com/carta/ink/pull/3754
*/

const ribbonVariants = {
  'feedback-positive': {
    background: newTableTokens.RibbonBackgroundColorPositiveDefault,
    border: newTableTokens.RibbonBorderColorPositiveDefault,
    font: newTableTokens.RibbonFontColorPositiveDefault,
  },
  'feedback-negative': {
    background: newTableTokens.RibbonBackgroundColorNegativeDefault,
    border: newTableTokens.RibbonBorderColorNegativeDefault,
    font: newTableTokens.RibbonFontColorNegativeDefault,
  },
  'feedback-notice': {
    background: newTableTokens.RibbonBackgroundColorNoticeDefault,
    border: newTableTokens.RibbonBorderColorNoticeDefault,
    font: newTableTokens.RibbonFontColorNoticeDefault,
  },
  'feedback-pending': {
    background: newTableTokens.RibbonBackgroundColorPendingDefault,
    border: newTableTokens.RibbonBorderColorPendingDefault,
    font: newTableTokens.RibbonFontColorPendingDefault,
  },
  'feedback-informational': {
    background: newTableTokens.RibbonBackgroundColorInformationalDefault,
    border: newTableTokens.RibbonBorderColorInformationalDefault,
    font: newTableTokens.RibbonFontColorInformationalDefault,
  },
  'feedback-staff': {
    background: newTableTokens.RibbonBackgroundColorStaffDefault,
    border: newTableTokens.RibbonBorderColorStaffDefault,
    font: newTableTokens.RibbonFontColorStaffDefault,
  },
  'feedback-neutral': {
    background: newTableTokens.RibbonBackgroundColorNeutralDefault,
    border: newTableTokens.RibbonBorderColorNeutralDefault,
    font: newTableTokens.RibbonFontColorNeutralDefault,
  },
};

const ribbonColors = {
  green: {
    background: newTableTokens.RibbonBackgroundColorGreenDefault,
    border: newTableTokens.RibbonBorderColorGreenDefault,
    font: newTableTokens.RibbonFontColorGreenDefault,
  },
  red: {
    background: newTableTokens.RibbonBackgroundColorRedDefault,
    border: newTableTokens.RibbonBorderColorRedDefault,
    font: newTableTokens.RibbonFontColorRedDefault,
  },
  blue: {
    background: newTableTokens.RibbonBackgroundColorBlueDefault,
    border: newTableTokens.RibbonBorderColorBlueDefault,
    font: newTableTokens.RibbonFontColorBlueDefault,
  },
  orange: {
    background: newTableTokens.RibbonBackgroundColorOrangeDefault,
    border: newTableTokens.RibbonBorderColorOrangeDefault,
    font: newTableTokens.RibbonFontColorOrangeDefault,
  },
  yellow: {
    background: newTableTokens.RibbonBackgroundColorYellowDefault,
    border: newTableTokens.RibbonBorderColorYellowDefault,
    font: newTableTokens.RibbonFontColorYellowDefault,
  },
  gray: {
    background: newTableTokens.RibbonBackgroundColorGrayDefault,
    border: newTableTokens.RibbonBorderColorGrayDefault,
    font: newTableTokens.RibbonFontColorGrayDefault,
  },
  pink: {
    background: newTableTokens.RibbonBackgroundColorPinkDefault,
    border: newTableTokens.RibbonBorderColorPinkDefault,
    font: newTableTokens.RibbonFontColorPinkDefault,
  },
};

const hoverSelectors = {
  row: `tr:hover &::before`,
  cell: `td:hover &::before`,
  self: `:hover::before`,
};

/* @internal */
export const StyledRibbon = styled.div<NewTableRibbonProps>`
  bottom: 0;
  left: 0;
  position: absolute;
  top: 0;
  width: 0;

  &::before {
    border-top-right-radius: ${newTableTokens.RibbonBorderRadiusBaseDefault};
    border-width: ${newTableTokens.RibbonBorderWidthBaseDefault};
    border-style: ${newTableTokens.RibbonBorderStyleBaseDefault};
    border-bottom-width: 4px;
    content: '${props => props.text}';
    font-size: ${newTableTokens.RibbonFontSizeBaseDefault};
    font-weight: ${newTableTokens.RibbonFontWeightBaseDefault};
    line-height: 1;
    opacity: 0;
    overflow: hidden;
    padding: ${newTableTokens.RibbonSpacingVerticalBaseDefault} ${newTableTokens.RibbonSpacingHorizontalBaseDefault};
    pointer-events: none;
    position: absolute;
    top: -31px;
    transform-origin: bottom left;
    transform: scale(0);
    transition: all 150ms 50ms ease-out;
    white-space: nowrap;

    ${({ theme }) => css`
      background-color: ${ribbonColors[theme.ribbonColor].background};
      border-color: ${ribbonColors[theme.ribbonColor].border};
      box-shadow: ${newTableTokens.RibbonShadowOffsetXBaseDefault} ${newTableTokens.RibbonShadowOffsetYBaseDefault}
        ${newTableTokens.RibbonShadowBlurBaseDefault} ${newTableTokens.RibbonShadowSpreadBaseDefault}
        ${newTableTokens.RibbonShadowColorBaseDefault};
      color: ${ribbonColors[theme.ribbonColor].font};
    `}

    ${({ theme }) =>
      theme.ribbonVariant &&
      css`
        background-color: ${ribbonVariants[theme.ribbonVariant].background};
        border-color: ${ribbonVariants[theme.ribbonVariant].border};
        color: ${ribbonVariants[theme.ribbonVariant].font};
      `};
  }

  // This code creates the triangle. These have not been converted to
  // tokens as it's not a design decision but a css trick.
  &::after {
    border-style: solid;
    border-width: 12px 12px 0 0;
    border-color: transparent;
    content: '';
    position: relative;
    top: 8px;
    width: 0;

    ${({ theme }) => css`
      border-top-color: ${ribbonColors[theme.ribbonColor].border};
    `}

    ${({ theme }) =>
      theme.ribbonVariant &&
      css`
        border-top-color: ${ribbonVariants[theme.ribbonVariant].border};
      `}
  }

  ${({ theme }) =>
    theme.ribbonHover &&
    css`
      ${hoverSelectors[theme.ribbonHover]} {
        opacity: 1;
        transform: scale(1);
      }

      // !!! Read notes on layering issues
      tbody > tr:first-child:hover &::before,
      tbody > tr:first-child td:hover &::before {
        z-index: ${elevationTokens.GlobalNewTableRibbon};
      }
    `}

  // Prevents ribbons from being displayed in Header cells
  ${StyledHeader} && {
    display: none;
  }
`;
